/**
 * Google Business Profile Adapter (Story 4.10)
 *
 * SECURITY: business.manage grants read+write. This adapter MUST only perform GET requests.
 * No POST/PUT/PATCH/DELETE operations are allowed.
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls and delegates discovery to
 * the existing discoverBusinessProfiles() function.
 *
 * FOUR APIs used:
 * - Business Profile Performance API v1 — metrics (impressions, actions)
 * - My Business Account Management API v1 — account listing, validation
 * - My Business Business Information API v1 — location listing, aggregate review stats
 * - Legacy My Business API v4 — individual reviews
 *
 * ADR-013: Adapter Pattern for API Isolation
 * ADR-014: Defensive API Versioning (versions via API_VERSIONS)
 *
 * OAuth is NOT part of this adapter — handled by Story 4.5.
 * Token refresh is NOT implemented — deferred to Story 4.12+.
 * KV caching is NOT implemented — deferred to Story 4.11.
 */

import type {
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  DimensionDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { discoverBusinessProfiles } from "./discovery";
import { API_VERSIONS } from "../config";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "google-business-profile" as const;

/** API base URLs — constructed using API_VERSIONS, NO hardcoded version strings */
const PERFORMANCE_BASE = `https://businessprofileperformance.googleapis.com/${API_VERSIONS.businessProfilePerformance}`;
// WARNING: Legacy v4 Reviews API is at HIGH sunset risk. Google already sunset the Q&A API
// (Nov 2025) from the same legacy endpoint. No replacement announced for reviews yet.
// Monitor https://developers.google.com/my-business/content/sunset-dates for changes.
const REVIEWS_BASE = `https://mybusiness.googleapis.com/${API_VERSIONS.mybusinessLegacy}`;

/** Per-call timeout for performance/review queries */
const QUERY_TIMEOUT_MS = 5_000;

/** Validation timeout (5s for lightweight Account Management API call) */
const VALIDATE_TIMEOUT_MS = 5_000;


// ============================================================================
// Date Range Parameter Constants (AC #10)
// ============================================================================

const PARAM_START_YEAR = "daily_range.start_date.year";
const PARAM_START_MONTH = "daily_range.start_date.month";
const PARAM_START_DAY = "daily_range.start_date.day";
const PARAM_END_YEAR = "daily_range.end_date.year";
const PARAM_END_MONTH = "daily_range.end_date.month";
const PARAM_END_DAY = "daily_range.end_date.day";

// ============================================================================
// Performance Metrics (AC #3, #13)
// ============================================================================

/**
 * GBP daily metrics to request.
 * DEPRECATED: PHOTOS_VIEWS_MERCHANT and PHOTOS_COUNT_CUSTOMERS are NOT included.
 * Photo view metrics were deprecated in 2025. Do NOT re-add them — the API will
 * return errors for these metrics.
 */
const GBP_DAILY_METRICS = [
  "BUSINESS_IMPRESSIONS_DESKTOP_MAPS",
  "BUSINESS_IMPRESSIONS_DESKTOP_SEARCH",
  "BUSINESS_IMPRESSIONS_MOBILE_MAPS",
  "BUSINESS_IMPRESSIONS_MOBILE_SEARCH",
  "CALL_CLICKS",
  "BUSINESS_DIRECTION_REQUESTS",
  "WEBSITE_CLICKS",
  "BUSINESS_CONVERSATIONS",
  "BUSINESS_BOOKINGS",
  "BUSINESS_FOOD_ORDERS",
  "BUSINESS_FOOD_MENU_CLICKS",
] as const;

// ============================================================================
// Star Rating Mapping (AC #4, #15)
// ============================================================================

/**
 * Star-rating enum → 1-5 integer mapping. Exported (Story 23.1 AC #7) so the
 * google-reviews adapter can reuse this single source of truth instead of
 * redeclaring it — duplication would diverge if Google ever adds a 6th rating.
 */
export const STAR_RATING_MAP: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

// ============================================================================
// Response Types (local to adapter, NOT exported)
// ============================================================================

interface PerformanceResponse {
  multiDailyMetricTimeSeries?: Array<{
    dailyMetricTimeSeries?: Array<{
      dailyMetric?: string;
      timeSeries?: {
        datedValues?: Array<{
          date?: { year?: number; month?: number; day?: number };
          value?: string;
        }>;
      } | null;
    }>;
  }>;
}

/**
 * Shape of Google v4 reviews response. Exported (Story 23.1 AC #7) for the
 * google-reviews adapter — extended there with `reviewer.isAnonymous` and
 * `reviewReply.reviewReplyState` (the latter shipped 2026-04-01).
 */
export interface ReviewsResponse {
  reviews?: Array<{
    reviewId?: string;
    reviewer?: {
      displayName?: string;
      profilePhotoUrl?: string;
    };
    starRating?: string;
    comment?: string;
    createTime?: string;
    updateTime?: string;
    reviewReply?: {
      comment?: string;
      updateTime?: string;
    };
  }>;
  averageRating?: number;
  totalReviewCount?: number;
  nextPageToken?: string;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build standard GBP API headers (Bearer token + Accept).
 * Exported (Story 23.1 AC #7) for the google-reviews adapter.
 */
export function buildHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/json",
  };
}

/**
 * Sanitize review text: strip HTML tags, control characters, Unicode bidi overrides,
 * normalize whitespace, and truncate at word boundary.
 * Exported (Story 23.1 AC #7) for the google-reviews adapter.
 */
export function sanitizeReviewText(text: string | undefined | null, maxLength: number): string {
  if (!text) return "";

  let sanitized = text;

  // Strip HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, "");

  // Strip control characters (except standard whitespace: space, tab, newline, carriage return)
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");

  // Strip Unicode bidirectional overrides (U+200E-U+200F, U+202A-U+202E, U+2066-U+2069)
  sanitized = sanitized.replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "");

  // Normalize whitespace
  sanitized = sanitized.replace(/\s+/g, " ").trim();

  // Truncate at word boundary
  return truncateAtWord(sanitized, maxLength);
}

/**
 * Truncate text at a word boundary, ensuring it doesn't exceed maxLength.
 * Exported (Story 23.1 AC #7) for the google-reviews adapter.
 */
export function truncateAtWord(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;

  // Reserve 3 chars for "..." suffix so total output never exceeds maxLength
  const limit = maxLength - 3;
  const truncated = text.slice(0, limit);
  const lastSpace = truncated.lastIndexOf(" ");

  if (lastSpace > limit * 0.5) {
    return truncated.slice(0, lastSpace) + "...";
  }

  return truncated + "...";
}

/**
 * Redact GBP resource paths from error messages before returning.
 * Prevents account/location enumeration via error messages.
 * Exported (Story 23.1 AC #7) for the google-reviews adapter.
 */
export function redactGbpPaths(message: string): string {
  return message.replace(
    /accounts\/\d+\/locations\/\d+/g,
    "accounts/[REDACTED]/locations/[REDACTED]"
  );
}

/**
 * Check if a 403 response is actually a quota exhaustion or API not enabled error.
 * GBP returns 403 for quota exhaustion and API not enabled (not 429).
 * Exported (Story 23.1 AC #7) for the google-reviews adapter.
 */
export function classify403Error(providerMessage: string | undefined): {
  code: "rate_limited" | "api_not_enabled";
  message: string;
} | null {
  if (!providerMessage) return null;

  // API not enabled (AC #5)
  if (
    providerMessage.includes("has not been used") ||
    providerMessage.includes("SERVICE_DISABLED")
  ) {
    return {
      code: "api_not_enabled",
      message:
        "Google Business Profile API is not enabled for this project. Please enable it in the Google Cloud Console.",
    };
  }

  // Quota exhaustion
  if (
    providerMessage.includes("quotaExceeded") ||
    providerMessage.includes("rateLimitExceeded")
  ) {
    return {
      code: "rate_limited",
      message: "Rate limit reached. Please try again later.",
    };
  }

  return null;
}

/**
 * Decompose a Date into UTC year/month/day integers.
 * Month is 1-indexed (January = 1). No zero-padding.
 */
function decomposeDateUTC(date: Date): {
  year: number;
  month: number;
  day: number;
} {
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

// ============================================================================
// Available Metrics Definition (AC #13)
// ============================================================================

/**
 * GBP metric definitions.
 * DEPRECATED photo views NOT included — see AC #13.
 */
const GBP_METRICS: MetricDefinition[] = [
  // Performance API — Daily Metrics (11)
  { key: "desktopMapsImpressions", label: "Desktop Maps Impressions", type: "number", category: "visibility", dataType: "Integer", aggregationKind: "additive" },
  { key: "desktopSearchImpressions", label: "Desktop Search Impressions", type: "number", category: "visibility", dataType: "Integer", aggregationKind: "additive" },
  { key: "mobileMapsImpressions", label: "Mobile Maps Impressions", type: "number", category: "visibility", dataType: "Integer", aggregationKind: "additive" },
  { key: "mobileSearchImpressions", label: "Mobile Search Impressions", type: "number", category: "visibility", dataType: "Integer", aggregationKind: "additive" },
  { key: "callClicks", label: "Call Button Clicks", type: "number", category: "actions", dataType: "Integer", aggregationKind: "additive" },
  { key: "directionRequests", label: "Direction Requests", type: "number", category: "actions", dataType: "Integer", aggregationKind: "additive" },
  { key: "websiteClicks", label: "Website Clicks", type: "number", category: "actions", dataType: "Integer", aggregationKind: "additive" },
  { key: "conversations", label: "Message Conversations", type: "number", category: "actions", dataType: "Integer", aggregationKind: "additive" },
  { key: "bookings", label: "Reserve Bookings", type: "number", category: "actions", dataType: "Integer", aggregationKind: "additive" },
  { key: "foodOrders", label: "Food Orders", type: "number", category: "actions", dataType: "Integer", aggregationKind: "additive" },
  { key: "foodMenuClicks", label: "Food Menu Clicks", type: "number", category: "actions", dataType: "Integer", aggregationKind: "additive" },
  // Derived/Aggregate Metrics (4)
  { key: "totalImpressions", label: "Total Impressions", type: "number", category: "visibility", dataType: "Integer", aggregationKind: "additive" },
  { key: "mapsImpressionsAll", label: "Maps Impressions (All)", type: "number", category: "visibility", dataType: "Integer", aggregationKind: "additive" },
  { key: "searchImpressionsAll", label: "Search Impressions (All)", type: "number", category: "visibility", dataType: "Integer", aggregationKind: "additive" },
  { key: "totalActions", label: "Total Actions", type: "number", category: "actions", dataType: "Integer", aggregationKind: "additive" },
  // Reviews — Aggregate (existing)
  { key: "totalReviewCount", label: "Total Reviews", type: "number", category: "reviews", dataType: "Integer", aggregationKind: "additive" },
  { key: "averageRating", label: "Average Rating", type: "number", category: "reviews", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "totalReviewCount" } },
  // Reviews — Period & Distribution (IMC-20)
  { key: "reviewsInPeriod", label: "Reviews in Period", type: "number", category: "reviews", dataType: "Integer", aggregationKind: "additive" },
  { key: "avgRatingInPeriod", label: "Avg Rating in Period", type: "number", category: "reviews", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "reviewsInPeriod" } },
  { key: "replyRate", label: "Reply Rate", type: "percentage", category: "reviews", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "totalReviewCount" } },
  { key: "fiveStarCount", label: "5-Star Reviews", type: "number", category: "reviews", dataType: "Integer", aggregationKind: "additive" },
  { key: "fourStarCount", label: "4-Star Reviews", type: "number", category: "reviews", dataType: "Integer", aggregationKind: "additive" },
  { key: "threeStarCount", label: "3-Star Reviews", type: "number", category: "reviews", dataType: "Integer", aggregationKind: "additive" },
  { key: "twoStarCount", label: "2-Star Reviews", type: "number", category: "reviews", dataType: "Integer", aggregationKind: "additive" },
  { key: "oneStarCount", label: "1-Star Reviews", type: "number", category: "reviews", dataType: "Integer", aggregationKind: "additive" },
  // Search Keywords (IMC-20)
  { key: "keywordImpressions", label: "Keyword Impressions", type: "number", category: "visibility", dataType: "Integer", aggregationKind: "additive" },
];

// ============================================================================
// Metric Key Mapping
// ============================================================================

const DAILY_METRIC_TO_KEY: Record<string, string> = {
  BUSINESS_IMPRESSIONS_DESKTOP_MAPS: "desktopMapsImpressions",
  BUSINESS_IMPRESSIONS_DESKTOP_SEARCH: "desktopSearchImpressions",
  BUSINESS_IMPRESSIONS_MOBILE_MAPS: "mobileMapsImpressions",
  BUSINESS_IMPRESSIONS_MOBILE_SEARCH: "mobileSearchImpressions",
  CALL_CLICKS: "callClicks",
  BUSINESS_DIRECTION_REQUESTS: "directionRequests",
  WEBSITE_CLICKS: "websiteClicks",
  BUSINESS_CONVERSATIONS: "conversations",
  BUSINESS_BOOKINGS: "bookings",
  BUSINESS_FOOD_ORDERS: "foodOrders",
  BUSINESS_FOOD_MENU_CLICKS: "foodMenuClicks",
};

// ============================================================================
// Query Execution
// ============================================================================

/**
 * Fetch performance metrics via fetchMultiDailyMetricsTimeSeries.
 * Uses repeated dailyMetrics query params (one per metric via URLSearchParams.append).
 * Date range uses component integer fields, NOT ISO 8601 strings.
 */
async function fetchPerformanceMetrics(
  locationId: string,
  dateRange: { start: Date; end: Date },
  headers: Record<string, string>
): Promise<AdapterResult<Record<string, unknown>[]>> {
  const startParts = decomposeDateUTC(dateRange.start);
  const endParts = decomposeDateUTC(dateRange.end);

  const params = new URLSearchParams();

  // Date range as component integer fields (AC #10)
  params.set(PARAM_START_YEAR, String(startParts.year));
  params.set(PARAM_START_MONTH, String(startParts.month));
  params.set(PARAM_START_DAY, String(startParts.day));
  params.set(PARAM_END_YEAR, String(endParts.year));
  params.set(PARAM_END_MONTH, String(endParts.month));
  params.set(PARAM_END_DAY, String(endParts.day));

  // Metrics as repeated query params (AC #3) — one .append() per metric, NOT comma-separated
  for (const metric of GBP_DAILY_METRICS) {
    params.append("dailyMetrics", metric);
  }

  // Note: :fetchMultiDailyMetricsTimeSeries is a Google custom method convention —
  // the colon is part of the URL path, NOT a query param
  const url = `${PERFORMANCE_BASE}/locations/${locationId}:fetchMultiDailyMetricsTimeSeries?${params.toString()}`;

  const result = await safeFetch(url, { headers }, QUERY_TIMEOUT_MS);

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      const classified = classify403Error(result.error.providerMessage);
      if (classified) {
        return adapterErr({
          ...result.error,
          code: classified.code,
          message: redactGbpPaths(classified.message),
        });
      }
    }
    return adapterErr({
      ...result.error,
      message: redactGbpPaths(result.error.message),
    });
  }

  try {
    const data = (await result.data.json()) as PerformanceResponse;

    const records: Record<string, unknown>[] = [];

    // Parse the nested response with optional chaining at every level
    const seriesList =
      data.multiDailyMetricTimeSeries?.[0]?.dailyMetricTimeSeries ?? [];

    for (const series of seriesList) {
      const metricName = series.dailyMetric;
      const metricKey = metricName
        ? DAILY_METRIC_TO_KEY[metricName]
        : undefined;

      if (!metricKey) continue;

      // timeSeries may be null (not just empty datedValues) when feature is not enabled
      const datedValues = series.timeSeries?.datedValues ?? [];

      for (const dv of datedValues) {
        const date = dv.date;
        if (!date?.year || !date?.month || !date?.day) continue;

        const dateStr = `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;

        // Values are strings — parse with Number(value) || 0
        const value = Number(dv.value) || 0;

        // Find or create a record for this date
        let record = records.find(
          (r) => r._date === dateStr
        ) as Record<string, unknown> | undefined;

        if (!record) {
          record = { _reportType: "performance", _date: dateStr };
          records.push(record);
        }

        record[metricKey] = value;
      }
    }

    // Compute derived metrics for each performance record (IMC-20 Task 1)
    for (const record of records) {
      const desktopMaps = (record.desktopMapsImpressions as number) || 0;
      const mobileMaps = (record.mobileMapsImpressions as number) || 0;
      const desktopSearch = (record.desktopSearchImpressions as number) || 0;
      const mobileSearch = (record.mobileSearchImpressions as number) || 0;

      record.totalImpressions = desktopMaps + mobileMaps + desktopSearch + mobileSearch;
      record.mapsImpressionsAll = desktopMaps + mobileMaps;
      record.searchImpressionsAll = desktopSearch + mobileSearch;

      const directions = (record.directionRequests as number) || 0;
      const calls = (record.callClicks as number) || 0;
      const website = (record.websiteClicks as number) || 0;
      const messages = (record.conversations as number) || 0;
      const bookings = (record.bookings as number) || 0;

      record.totalActions = directions + calls + website + messages + bookings;
    }

    return adapterOk(records);
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: redactGbpPaths(
        sanitizeErrorMessage(
          `Failed to parse GBP performance response: ${err instanceof Error ? err.message : String(err)}`
        )
      ),
      retryable: false,
    });
  }
}

/**
 * Fetch reviews from the legacy v4 API.
 * Uses BOTH parentAccountId AND locationId (they are different numbers).
 * Only fetches page 1 (pageSize=50) for Worker timeout budget.
 *
 * Returns aggregate stats (totalReviewCount, averageRating) from the
 * reviews response, plus individual review data.
 */
async function fetchReviews(
  parentAccountId: string,
  locationId: string,
  headers: Record<string, string>
): Promise<
  AdapterResult<{
    reviews: Record<string, unknown>[];
    totalReviewCount: number;
    averageRating: number;
  }>
> {
  // Validate resource name format before use (AC #4)
  const resourcePath = `accounts/${parentAccountId}/locations/${locationId}`;
  if (!/^accounts\/\d+\/locations\/\d+$/.test(resourcePath)) {
    return adapterErr({
      code: "invalid_response",
      message: "Invalid GBP resource path for reviews",
      retryable: false,
    });
  }

  // Reviews v4 URL MUST use explicit accounts/{id} — wildcard NOT supported (AC #4)
  const url = `${REVIEWS_BASE}/${resourcePath}/reviews?pageSize=50`;

  const result = await safeFetch(url, { headers }, QUERY_TIMEOUT_MS);

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      const classified = classify403Error(result.error.providerMessage);
      if (classified) {
        return adapterErr({
          ...result.error,
          code: classified.code,
          message: redactGbpPaths(classified.message),
        });
      }
    }
    return adapterErr({
      ...result.error,
      message: redactGbpPaths(result.error.message),
    });
  }

  try {
    const data = (await result.data.json()) as ReviewsResponse;

    const reviews: Record<string, unknown>[] = (data.reviews ?? []).map(
      (review) => ({
        _reportType: "review",
        reviewId: review.reviewId ?? "",
        reviewerName: sanitizeReviewText(review.reviewer?.displayName, 100),
        starRating: STAR_RATING_MAP[review.starRating ?? ""] ?? 0,
        comment: sanitizeReviewText(review.comment, 300),
        createTime: review.createTime ?? "",
        updateTime: review.updateTime ?? "",
        replyComment: review.reviewReply
          ? sanitizeReviewText(review.reviewReply.comment, 300)
          : undefined,
      })
    );

    return adapterOk({
      reviews,
      // Use aggregate stats from the reviews response (AC #4)
      totalReviewCount: data.totalReviewCount ?? 0,
      averageRating: data.averageRating ?? 0,
    });
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: redactGbpPaths(
        sanitizeErrorMessage(
          `Failed to parse GBP reviews response: ${err instanceof Error ? err.message : String(err)}`
        )
      ),
      retryable: false,
    });
  }
}

// ============================================================================
// Search Keywords (IMC-20 Task 3)
// ============================================================================

/**
 * Search keywords endpoint response shape.
 * Uses the Business Profile Performance API v1 (same as daily metrics).
 * Endpoint: locations/{id}/searchkeywords/impressions/monthly
 * Returns monthly keyword impression data. Privacy threshold: terms with <15
 * searches return threshold value instead of actual count.
 */
interface SearchKeywordsResponse {
  searchKeywordsCounts?: Array<{
    searchKeyword?: string;
    insightsValue?: { value?: string; threshold?: string };
  }>;
  nextPageToken?: string;
}

/**
 * Fetch monthly search keyword impressions from the Performance API v1.
 * Uses the same API base as daily metrics (NOT the legacy v4 API).
 * Returns empty array on failure (graceful degradation).
 */
async function fetchSearchKeywords(
  _parentAccountId: string,
  locationId: string,
  headers: Record<string, string>
): Promise<Record<string, unknown>[]> {
  // Validate location ID format
  if (!/^\d{5,15}$/.test(locationId)) {
    return [];
  }

  const url = `${PERFORMANCE_BASE}/locations/${locationId}/searchkeywords/impressions/monthly`;

  try {
    const result = await safeFetch(url, { headers }, QUERY_TIMEOUT_MS);

    if (!result.success) {
      // Expected failure — endpoint is deprecated for most accounts
      return [];
    }

    const data = (await result.data.json()) as SearchKeywordsResponse;
    const keywords = data.searchKeywordsCounts ?? [];

    if (keywords.length === 0) return [];

    // Sort by impressions descending, take top 20
    const sorted = keywords
      .map((kw) => ({
        keyword: (kw.searchKeyword ?? "").toLowerCase(),
        impressions: Number(kw.insightsValue?.value) || 0,
      }))
      .filter((kw) => kw.keyword && kw.impressions > 0)
      .sort((a, b) => b.impressions - a.impressions);

    const top20 = sorted.slice(0, 20);
    const otherImpressions = sorted.slice(20).reduce((sum, kw) => sum + kw.impressions, 0);

    const records: Record<string, unknown>[] = top20.map((kw) => ({
      _reportType: "searchKeywords",
      search_keyword: kw.keyword,
      keywordImpressions: kw.impressions,
    }));

    if (otherImpressions > 0) {
      records.push({
        _reportType: "searchKeywords",
        search_keyword: "other",
        keywordImpressions: otherImpressions,
      });
    }

    return records;
  } catch {
    // Graceful degradation — keyword data is optional
    return [];
  }
}

// ============================================================================
// Available Dimensions Definition (Story 5.34)
// ============================================================================

const GBP_DIMENSIONS: DimensionDefinition[] = [
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01"] },
  // IMC-20: New dimensions
  { key: "day_of_week", label: "Day of Week", type: "Time", exampleValues: ["Monday", "Tuesday"] },
  { key: "time_of_day", label: "Time of Day", type: "Time", exampleValues: ["9", "14"] },
  { key: "star_rating", label: "Star Rating", type: "Review", exampleValues: ["5", "4", "3"] },
  { key: "has_reply", label: "Has Reply", type: "Review", exampleValues: ["true", "false"] },
  { key: "reviewer_name", label: "Reviewer Name", type: "Review", exampleValues: ["John D."] },
  { key: "review_date", label: "Review Date", type: "Time", exampleValues: ["2026-03-15"] },
  { key: "search_keyword", label: "Search Keyword", type: "Search", exampleValues: ["plumber near me"] },
  { key: "month", label: "Month", type: "Time", exampleValues: ["2026-03"] },
];

// ============================================================================
// Google Business Profile Adapter (IntegrationAdapter implementation)
// ============================================================================

export const gbpAdapter: IntegrationAdapter = {
  platform: PLATFORM,

  rateLimits: {
    requestsPerMinute: 300,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const accountId = ctx.accountId;

    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message:
          "No GBP location selected. Please select a business location first.",
        retryable: false,
      });
    }

    const headers = buildHeaders(credentials.accessToken);

    // 3 sequential queries — fail-fast on first error (AC #11)
    // Query 1: Performance metrics
    const performanceResult = await fetchPerformanceMetrics(
      accountId,
      dateRange,
      headers
    );
    if (!performanceResult.success) {
      return adapterErr(performanceResult.error);
    }

    // Query 2: Read parentAccountId from KV for Reviews API
    let parentAccountId: string | undefined;
    try {
      const kv = ctx.env.KV as { get(key: string): Promise<string | null> };
      const metaRaw = await kv.get(`gbp_meta:${ctx.integrationId}`);
      if (metaRaw) {
        const meta = JSON.parse(metaRaw) as { parentAccountId?: string };
        parentAccountId = meta.parentAccountId;
      }
    } catch {
      // KV read failed — continue without parentAccountId
    }

    // Query 3: Reviews from legacy v4 API (hard failure — reviews are required, AC #11)
    let reviewData: {
      reviews: Record<string, unknown>[];
      totalReviewCount: number;
      averageRating: number;
    };

    if (parentAccountId) {
      const reviewsResult = await fetchReviews(
        parentAccountId,
        accountId,
        headers
      );
      if (!reviewsResult.success) {
        return adapterErr(reviewsResult.error);
      }
      reviewData = reviewsResult.data;
    } else {
      // No parentAccountId available — cannot call Reviews v4 API (requires both IDs).
      // Return zeros; review data will be available after parentAccountId is stored via PUT /account.
      reviewData = {
        reviews: [],
        totalReviewCount: 0,
        averageRating: 0,
      };
    }

    // IMC-20 Task 3: Search keywords (optional — endpoint is deprecated)
    let searchKeywordRecords: Record<string, unknown>[] = [];
    if (parentAccountId) {
      searchKeywordRecords = await fetchSearchKeywords(
        parentAccountId,
        accountId,
        headers
      );
    }

    // IMC-20 Task 2: Aggregate review star distribution + period metrics
    const starCounts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let reviewsInPeriod = 0;
    let ratingSum = 0;
    let repliedCount = 0;

    for (const review of reviewData.reviews) {
      const star = review.starRating as number;
      if (star >= 1 && star <= 5) {
        starCounts[star]!++;
      }

      // Filter reviews by date range for period metrics
      const createTime = review.createTime as string;
      if (createTime && star >= 1 && star <= 5) {
        const reviewDate = new Date(createTime);
        if (reviewDate >= dateRange.start && reviewDate <= dateRange.end) {
          reviewsInPeriod++;
          ratingSum += star;
        }
      }

      if (review.replyComment !== undefined) {
        repliedCount++;
      }
    }

    const avgRatingInPeriod = reviewsInPeriod > 0
      ? Math.round((ratingSum / reviewsInPeriod) * 10) / 10
      : 0;
    // Reply rate: repliedCount / fetched reviews (page 1, up to 50).
    // Uses fetched count (not API totalReviewCount) because we can only count
    // replies in reviews we actually retrieved.
    const fetchedReviewCount = reviewData.reviews.length;
    const replyRate = fetchedReviewCount > 0
      ? Math.round((repliedCount / fetchedReviewCount) * 1000) / 10
      : 0;

    // Star distribution records (IMC-20 AC #2)
    const reviewsByRatingRecords: Record<string, unknown>[] = [5, 4, 3, 2, 1].map(
      (star) => ({
        _reportType: "reviewsByRating",
        star_rating: star,
        review_count: starCounts[star]!,
      })
    );

    // Combine all records
    const allRecords: Record<string, unknown>[] = [
      ...performanceResult.data,
      {
        _reportType: "reviewSummary",
        totalReviewCount: reviewData.totalReviewCount,
        averageRating: reviewData.averageRating,
        reviewsInPeriod,
        avgRatingInPeriod,
        replyRate,
        fiveStarCount: starCounts[5],
        fourStarCount: starCounts[4],
        threeStarCount: starCounts[3],
        twoStarCount: starCounts[2],
        oneStarCount: starCounts[1],
      },
      ...reviewsByRatingRecords,
      ...reviewData.reviews,
      ...searchKeywordRecords,
    ];

    console.log(
      JSON.stringify({
        event: "gbp_fetch_complete",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        performanceRecords: performanceResult.data.length,
        reviewCount: reviewData.reviews.length,
        totalReviewCount: reviewData.totalReviewCount,
        averageRating: reviewData.averageRating,
        reviewsByRatingRecords: reviewsByRatingRecords.length,
        searchKeywordRecords: searchKeywordRecords.length,
        recordCount: allRecords.length,
        timestamp: new Date().toISOString(),
      })
    );

    return adapterOk({
      platform: PLATFORM,
      accountId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records: allRecords,
      fetchedAt: new Date(),
      recordCount: allRecords.length,
    });
  },

  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    // Lightweight Account Management API call with pageSize=1 (AC #8)
    const url = `https://mybusinessaccountmanagement.googleapis.com/${API_VERSIONS.mybusinessAccountManagement}/accounts?pageSize=1`;
    const headers = buildHeaders(credentials.accessToken);

    const result = await safeFetch(url, { headers }, VALIDATE_TIMEOUT_MS);

    if (!result.success) {
      return adapterErr(result.error);
    }

    return adapterOk(true);
  },

  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Delegate to updated discoverBusinessProfiles() — now returns locations
    return discoverBusinessProfiles(credentials.accessToken);
  },

  getAvailableMetrics(): MetricDefinition[] {
    // DEPRECATED: Photo views NOT included (AC #13)
    // PHOTOS_VIEWS_MERCHANT and PHOTOS_COUNT_CUSTOMERS were deprecated in 2025.
    // Do NOT re-add them — the Performance API no longer supports these metrics.
    return GBP_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return GBP_DIMENSIONS;
  },
};
